/*
 * DialUpDuo Favorites page renderer — docs/plans/dialupduo-wishlist.md
 * Fills sections/duo-wishlist.liquid. Own list comes from window.DuoWishlist
 * (localStorage truth); shared view (?list=<uuid>) comes from the worker.
 * Cards are the theme's own markup via the Section Rendering API
 * (/products/{handle}?section_id=duo-wishlist-card), fetched 4 at a time into
 * ratio-reserving skeletons so nothing shifts.
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-duo-wishlist-page]');
  if (!root || !window.DuoWishlist) return;

  const CARD_SECTION = root.dataset.cardSection || 'duo-wishlist-card';
  const PAGE_SIZE = 24;
  const CONCURRENCY = 4;

  const grid = root.querySelector('[data-duo-wishlist-grid]');
  const emptyEl = root.querySelector('[data-duo-wishlist-empty]');
  const banner = root.querySelector('[data-duo-wishlist-banner]');
  const shareButton = root.querySelector('[data-duo-wishlist-share]');
  const moreButton = root.querySelector('[data-duo-wishlist-more]');
  const countEl = root.querySelector('[data-duo-wishlist-page-count]');

  const params = new URLSearchParams(window.location.search);
  const requestedList = String(params.get('list') || '').toLowerCase();
  const mode = requestedList && requestedList !== window.DuoWishlist.listId() ? 'shared' : 'own';

  let items = [];
  let rendered = 0;

  function showBanner(html) {
    banner.innerHTML = html;
    banner.hidden = false;
  }

  function ownSet() {
    return new Set(window.DuoWishlist.items().map((item) => String(item.id)));
  }

  function updateChrome() {
    const count = items.length;
    countEl.textContent = count > 0 ? `${count} ${count === 1 ? 'item' : 'items'} saved` : '';
    shareButton.hidden = mode !== 'own' || count === 0;
    moreButton.hidden = rendered >= count;
    emptyEl.hidden = !(mode === 'own' && count === 0);
    if (mode === 'own' && count > 0 && root.dataset.loggedIn !== 'true') {
      showBanner(
        `Log in to keep your Favorites on all your devices. <a href="${root.dataset.loginUrl || '/account/login'}">Log in</a>`
      );
    }
  }

  function renderUnavailable(li, item) {
    const stub = document.createElement('div');
    stub.className = 'duo-wishlist-page__unavailable';
    const title = document.createElement('strong');
    title.textContent = item.handle.replace(/-/g, ' ');
    const note = document.createElement('span');
    note.textContent = 'This product is no longer available.';
    stub.appendChild(title);
    stub.appendChild(note);
    if (mode === 'own') {
      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.textContent = 'Remove';
      removeButton.addEventListener('click', () => window.DuoWishlist.remove(item.id));
      stub.appendChild(removeButton);
    }
    li.appendChild(stub);
  }

  async function fillSlot(li, item) {
    try {
      const response = await fetch(
        `/products/${encodeURIComponent(item.handle)}?section_id=${encodeURIComponent(CARD_SECTION)}`,
        { headers: { Accept: 'text/html' } }
      );
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = doc.querySelector('[data-duo-wishlist-card]');
      if (!card) throw new Error('Card markup missing');
      li.innerHTML = '';
      li.appendChild(document.importNode(card, true));
      // Crossfade the real card in over its skeleton (CSS gates on reduced motion).
      li.classList.add('duo-wishlist-page__card-in');
    } catch (error) {
      li.innerHTML = '';
      renderUnavailable(li, item);
    }
  }

  function makeSlot(item, prepend) {
    const li = document.createElement('li');
    li.dataset.duoWishlistItem = item.id;
    const skeleton = document.createElement('div');
    skeleton.className = 'duo-wishlist-page__skeleton';
    li.appendChild(skeleton);
    if (prepend && grid.firstChild) {
      grid.insertBefore(li, grid.firstChild);
    } else {
      grid.appendChild(li);
    }
    return li;
  }

  async function pool(tasks, limit) {
    const queue = tasks.slice();
    const workers = Array.from({ length: Math.min(limit, queue.length) }, async () => {
      while (queue.length) await queue.shift()();
    });
    await Promise.all(workers);
  }

  async function renderNextPage() {
    const batch = items.slice(rendered, rendered + PAGE_SIZE);
    rendered += batch.length;
    const slots = batch.map((item) => ({ item, li: makeSlot(item, false) }));
    updateChrome();
    await pool(slots.map(({ item, li }) => () => fillSlot(li, item)), CONCURRENCY);
  }

  async function loadItems() {
    if (mode === 'shared') {
      try {
        const response = await fetch(
          `${window.DuoWishlist.proxyRoot}/wishlist?list=${encodeURIComponent(requestedList)}`,
          { headers: { Accept: 'application/json' }, credentials: 'same-origin' }
        );
        const payload = response.ok ? await response.json() : null;
        items = Array.isArray(payload?.items) ? payload.items : [];
        showBanner(
          `You are viewing a shared Favorites list. <a href="${window.location.pathname}">View your own</a>`
        );
        if (items.length === 0) {
          showBanner('This shared Favorites list is empty or no longer available.');
        }
      } catch (error) {
        items = [];
        showBanner('This shared Favorites list could not be loaded.');
      }
    } else {
      items = window.DuoWishlist.items();
    }
    items = items
      .filter((item) => item && item.handle)
      .sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
  }

  /* Keep the own-mode grid in step with hearts (remove + undo re-add). */
  document.addEventListener('duo:wishlist:change', () => {
    if (mode !== 'own') return;
    const current = ownSet();
    let removedAny = false;
    grid.querySelectorAll('[data-duo-wishlist-item]').forEach((li) => {
      if (!current.has(String(li.dataset.duoWishlistItem))) {
        li.remove();
        removedAny = true;
      }
    });
    if (removedAny) {
      items = items.filter((item) => current.has(String(item.id)));
      rendered = Math.min(rendered, items.length);
    }
    const shown = new Set(
      [...grid.querySelectorAll('[data-duo-wishlist-item]')].map((li) => String(li.dataset.duoWishlistItem))
    );
    const fresh = window.DuoWishlist.items().filter((item) => !shown.has(String(item.id)));
    for (const item of fresh) {
      items.unshift(item);
      rendered += 1;
      fillSlot(makeSlot(item, true), item);
    }
    updateChrome();
  });

  shareButton.addEventListener('click', () => {
    const url = `${window.location.origin}${window.location.pathname}?list=${window.DuoWishlist.listId()}`;
    const copied = navigator.clipboard && navigator.clipboard.writeText
      ? navigator.clipboard.writeText(url).then(() => true).catch(() => false)
      : Promise.resolve(false);
    copied.then((ok) => {
      if (ok) {
        window.DuoWishlist.toast('Share link copied');
      } else {
        window.prompt('Copy this link to share your Favorites', url);
      }
    });
    window.DuoWishlist.syncNow();
  });

  moreButton.addEventListener('click', renderNextPage);

  loadItems().then(renderNextPage);
})();
